/**
 * Try-on frame catalogue loader.
 *
 * The Next.js app fetches this once at build time and seeds it through a context;
 * this app is a plain SPA, so the catalogue is fetched from the browser instead
 * and memoised in a module-level promise. Every try-on surface (the /tryon page,
 * the studio and the per-product modal) shares that single in-flight/settled
 * promise, so the catalogue is fetched at most once per page load.
 *
 * Because it lives that long, only what a frame IS is kept: price, discount and stock are
 * stripped, and the try-on stage fetches them live (useLivePricing) each time it opens.
 */

import { getFilteredProducts, stripCommerce } from '../../api/products';
import type { Product } from '../../types';
import { canTryOn } from './frameUtils';

const PAGE_SIZE = 200;
const MAX_PAGES = 200; // safety cap — stops a runaway loop on a bad `has_more`

let cataloguePromise: Promise<Product[]> | null = null;

/** Page the filter endpoint until exhausted, keeping try-on eligible frames. */
async function fetchTryonFrames(): Promise<Product[]> {
  const all: Product[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const { products, hasMore } = await getFilteredProducts({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    all.push(...products.map(stripCommerce));
    if (!hasMore || products.length === 0) break;
  }
  return all.filter(canTryOn);
}

/**
 * Frames eligible for virtual try-on, fetched once and cached for the session.
 * Never rejects — an API failure resolves to an empty list and is retried on the
 * next call (the failed promise is dropped) rather than caching the failure.
 */
export function loadTryonCatalogue(): Promise<Product[]> {
  if (!cataloguePromise) {
    cataloguePromise = fetchTryonFrames().catch(() => {
      cataloguePromise = null;
      return [];
    });
  }
  return cataloguePromise;
}
